import type { ErrorEvent, Exception, StackFrame } from '@sentry/gatsby'

// WebKit renders the message with a trailing period ("…exceeded."), V8 without. Match the shared
// phrase rather than either spelling, and unanchored so an engine prefix can't miss.
const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded/

const firstException = (event: ErrorEvent): Exception | undefined => event.exception?.values?.[0]

export const isStackOverflowEvent = (event: ErrorEvent): boolean => {
  const exception = firstException(event)
  return (
    exception?.type === 'RangeError' &&
    typeof exception.value === 'string' &&
    STACK_OVERFLOW_MESSAGE.test(exception.value)
  )
}

/**
 * Where the recursing code came from, judged from frame filenames.
 *
 * Do NOT reach for `frame.in_app` here: the browser stack parser hardcodes `in_app: true` on every
 * frame it produces (`@sentry/browser/build/.../stack-parsers.js`, "All browser frames are
 * considered in_app"), and the node_modules-vs-app judgement visible in the Sentry UI is made
 * server-side, after sourcemaps are applied — long after `beforeSend` has run. Classifying on
 * `in_app` client-side returns 'first-party' for everything.
 */
export type StackOverflowOrigin = 'injected' | 'first-party' | 'third-party'

export interface StackOverflowAttribution {
  readonly origin: StackOverflowOrigin
  /** Top frame's file, with a frame attributed to the document collapsed to 'document'. */
  readonly topFilename: string
  /** Distinct script origins across the stack, in first-seen order. */
  readonly frameOrigins: readonly string[]
}

// WebKit attributes an error thrown from runtime-injected script — eval, new Function, innerHTML,
// document.write — to the document URL rather than to a file. Those frames are the bulk of this
// issue and they are what fragments it: the filename is the page, so every URL mints a new group.
// Collapsing them to a single token is what makes the grouping stable and the volume visible.
const DOCUMENT_FILENAME = 'document'
const UNKNOWN_FILENAME = 'unknown'

// Every bundle we serve is a same-origin `.js`: www sets neither `assetPrefix` nor `pathPrefix`, and
// a Gatsby page path carries no file extension. So a same-origin frame that does not name a script
// file is the document.
const SCRIPT_PATHNAME = /\.m?js$/

// When WebKit reports no source URL at all for injected script, the SDK's onerror fallback frame
// stringifies it. DFH-CORP-SITES-DED (8k+ events, a lone `undefined:192:70` frame) is that shape
// of the same population, and without this it classified as 'third-party' and escaped the fingerprint.
const STRINGIFIED_MISSING_FILENAMES: ReadonlySet<string> = new Set(['undefined', 'null'])

const parseUrl = (value: string): URL | undefined => {
  try {
    return new URL(value)
  } catch {
    return undefined
  }
}

const pageOrigin = (): string | undefined =>
  typeof window === 'undefined' ? undefined : window.location.origin

// Matching the frame against the live `location.href` is not enough: WebKit attributes injected
// script to the document URL at INJECTION time, which does not follow history.pushState. www is a
// Gatsby SPA, so an overflow after a client-side navigation carries a stale page URL — same origin,
// different path. That frame would survive as a filename, classify as 'first-party', miss the pinned
// fingerprint and stay fragmented, in the bucket that is supposed to mean a recursion in our code.
const normalizeFilename = (filename: string | undefined, origin: string | undefined): string => {
  if (!filename) return UNKNOWN_FILENAME
  if (STRINGIFIED_MISSING_FILENAMES.has(filename)) return DOCUMENT_FILENAME
  const url = parseUrl(filename)
  return url !== undefined && url.origin === origin && !SCRIPT_PATHNAME.test(url.pathname)
    ? DOCUMENT_FILENAME
    : filename
}

// Group by origin rather than full URL so a hashed bundle name doesn't re-fragment what we just
// collapsed. Non-URL filenames (native frames, the document token) stand for themselves.
const originOf = (filename: string): string => parseUrl(filename)?.origin ?? filename

// Our bundles are served from the page's own origin, and everything we depend on is compiled into
// them — Radix included, which is why the proven focus-trap trace counts as ours. A third-party tag
// loads from its own host, and injected script has no file at all.
const classify = (
  normalized: readonly string[],
  origin: string | undefined,
): StackOverflowOrigin => {
  if (normalized.includes(DOCUMENT_FILENAME)) return 'injected'
  return origin !== undefined && normalized.some((filename) => originOf(filename) === origin)
    ? 'first-party'
    : 'third-party'
}

const framesOf = (exception: Exception): readonly StackFrame[] => exception.stacktrace?.frames ?? []

/**
 * Classify a stack-overflow event so it can be fingerprinted into one issue per population and sliced
 * by where the recursing code came from. Returns undefined for anything that is not a stack overflow.
 */
export const stackOverflowAttribution = (
  event: ErrorEvent,
): StackOverflowAttribution | undefined => {
  if (!isStackOverflowEvent(event)) return undefined

  const exception = firstException(event)
  const frames = exception === undefined ? [] : framesOf(exception)
  const origin = pageOrigin()
  // Sentry orders frames oldest-first, so the throwing frame is last.
  const normalized = frames.map((frame) => normalizeFilename(frame.filename, origin))

  return {
    origin: classify(normalized, origin),
    topFilename: normalized[normalized.length - 1] ?? UNKNOWN_FILENAME,
    frameOrigins: [...new Set(normalized.map(originOf))],
  }
}

export interface InjectedRuntimeEvidence {
  /** Whether Chrome for iOS's injected runtime (`window.__gCrWeb`) is on the page. */
  readonly gcrweb: boolean
  /** Its top-level module names (autofill, form, passwords, …), sorted, to narrow the culprit. */
  readonly modules: readonly string[]
  /** How many modules there were before the cap, so a truncated list is recognisable as one. */
  readonly moduleCount: number
}

// Well above the module count Chrome for iOS has been seen to install, so `translate`, which sorts
// near the end, is not cut; the cap only stops a context field growing unbounded.
const MAX_GCRWEB_MODULES = 50

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/**
 * Chrome for iOS and the Google app inject Chromium's own Closure-compiled user scripts into every
 * page under `window.__gCrWeb`. Mobile Safari does not. The injected overflows (sc-146245) recurse
 * through two-letter Closure names and never occur in Safari, so recording whether that runtime is
 * present on each overflow is what confirms or rules it out as the culprit. Only key names are read,
 * never values, so no autofill or form data leaves the page.
 */
export const injectedRuntimeEvidence = (win: unknown): InjectedRuntimeEvidence => {
  const gcrweb = isRecord(win) ? win.__gCrWeb : undefined
  if (!isRecord(gcrweb)) return { gcrweb: false, modules: [], moduleCount: 0 }
  const modules = Object.keys(gcrweb)
  return {
    gcrweb: true,
    modules: modules.sort().slice(0, MAX_GCRWEB_MODULES),
    moduleCount: modules.length,
  }
}

export interface TranslationEvidence {
  /** Whether the browser has machine-translated the page in place. */
  readonly translated: boolean
  /** The `<html lang>` at the time of the error, which translation may rewrite from what we served. */
  readonly lang: string
}

// Chrome's translate marks the root `translated-ltr`/`translated-rtl`, and on iOS it has been seen
// wrapping every text node in `<font style="vertical-align: inherit">`. Either one is enough: we
// never render a `<font>` ourselves, and the class is not ours either. The wrapper is matched on the
// property name alone because reports show the attribute both with and without the space.
const TRANSLATED_ROOT_CLASSES = ['translated-ltr', 'translated-rtl']
const TRANSLATED_TEXT_WRAPPER = 'font[style*="vertical-align"]'

/**
 * Other sites see this exact recursion on Chrome for iOS pages that auto-translate has rewritten,
 * so recording whether the page was translated when it overflowed tests that trigger directly. A
 * `__gCrWeb` check alone cannot, since the runtime is on every Chrome for iOS page.
 */
export const translationEvidence = (doc: Document | undefined): TranslationEvidence => {
  const root = doc?.documentElement
  if (!root) return { translated: false, lang: '' }
  const translated =
    TRANSLATED_ROOT_CLASSES.some((name) => root.classList.contains(name)) ||
    doc.querySelector(TRANSLATED_TEXT_WRAPPER) !== null
  return { translated, lang: root.lang }
}
